<!--
  Copyright (c) 2020-06-29 Qoder. All rights reserved.
-->
<template>
  <div class="tuition-fee">
    <!-- 模块未开放提示 -->
    <div v-if="!moduleVisible" class="module-closed">
      <a-result status="403" title="该功能暂未开放" sub-title="请联系学校管理员开通学费查询模块" />
    </div>

    <template v-else>
      <!-- 汇总卡片区 -->
      <div class="summary-cards">
        <a-row :gutter="16">
          <a-col :span="6">
            <div class="card-item">
              <div class="card-label">应收总额</div>
              <div class="card-value">{{ formatMoney(summary.receivableFee) }}</div>
            </div>
          </a-col>
          <a-col :span="6">
            <div class="card-item">
              <div class="card-label">已收总额</div>
              <div class="card-value paid">{{ formatMoney(summary.paidFee) }}</div>
            </div>
          </a-col>
          <a-col :span="6">
            <div class="card-item">
              <div class="card-label">退费总额</div>
              <div class="card-value refund">{{ formatMoney(summary.refundFee) }}</div>
            </div>
          </a-col>
          <a-col :span="6">
            <div class="card-item">
              <div class="card-label">欠费总额</div>
              <div class="card-value owe">{{ formatMoney(summary.oweFee) }}</div>
            </div>
          </a-col>
        </a-row>
      </div>

      <!-- 筛选区 -->
      <div class="filter-bar">
        <a-select v-model="filterYear" placeholder="收费年度" allow-clear style="width: 120px; margin-right: 12px" @change="handleSearch">
          <a-select-option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</a-select-option>
        </a-select>
        <j-dict-select-tag v-model="filterType" type="select" dictCode="stu_school_fee_type" placeholder="费项类型" style="width: 150px; margin-right: 12px" @change="handleSearch" />
        <a-button type="primary" @click="handleSearch">查询</a-button>
      </div>

      <!-- 明细列表 -->
      <div class="detail-table">
        <a-table
          :columns="columns"
          :data-source="detailList"
          :loading="loading"
          :pagination="pagination"
          @change="handleTableChange"
          rowKey="id"
          size="middle"
        >
          <template slot="amount" slot-scope="text, record">
            <span :style="{ color: record.isRefund === 1 ? '#52c41a' : '' }">
              {{ record.isRefund === 1 ? '-' : '' }}{{ text }}
            </span>
          </template>
          <template slot="isRefund" slot-scope="text">
            <a-tag :color="text === 1 ? 'green' : 'blue'">{{ text === 1 ? '退费' : '收费' }}</a-tag>
          </template>
        </a-table>
      </div>
    </template>
  </div>
</template>

<script>
import { getFeeSummary, getFeeDetails } from '@/api/fee';

export default {
  name: 'TuitionFee',
  data() {
    return {
      moduleVisible: true,
      summary: {},
      detailList: [],
      loading: false,
      filterYear: undefined,
      filterType: '',
      yearOptions: [],
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showTotal: total => `共 ${total} 条`,
      },
      columns: [
        { title: '序号', customRender: (text, record, index) => (this.pagination.current - 1) * this.pagination.pageSize + index + 1, width: 60, align: 'center' },
        { title: '收费日期', dataIndex: 'feeDate', align: 'center', width: 120 },
        { title: '费项类型', dataIndex: 'feeType_dictText', customRender: (text, record) => text || record.feeType, align: 'center', width: 150 },
        { title: '金额（元）', dataIndex: 'amount', scopedSlots: { customRender: 'amount' }, align: 'right', width: 120 },
        { title: '类型', dataIndex: 'isRefund', scopedSlots: { customRender: 'isRefund' }, align: 'center', width: 80 },
        { title: '收费年度', dataIndex: 'feeYear', align: 'center', width: 100 },
      ],
    };
  },
  created() {
    this.checkModule();
  },
  methods: {
    /** 检查模块是否开放 */
    checkModule() {
      const homeObj = this.$store.state.homeObj || {};
      if (homeObj.fee_module_visible !== '1') {
        this.moduleVisible = false;
        return;
      }
      this.moduleVisible = true;
      this.loadSummary();
      this.loadDetails();
    },

    /** 加载汇总数据 */
    async loadSummary() {
      try {
        const res = await getFeeSummary();
        if (res.code === 200) {
          this.summary = res.result || {};
        } else {
          this.$message.error(res.message || '查询汇总失败');
        }
      } catch (e) {
        this.$message.error('查询汇总失败');
      }
    },

    /** 加载明细列表 */
    async loadDetails() {
      this.loading = true;
      try {
        const params = {
          pageNo: this.pagination.current,
          pageSize: this.pagination.pageSize,
        };
        if (this.filterYear) params.feeYear = this.filterYear;
        if (this.filterType) params.feeType = this.filterType;

        const res = await getFeeDetails(params);
        if (res.code === 200) {
          const result = res.result || {};
          this.detailList = result.records || [];
          this.pagination.total = result.total || 0;
          // 提取年度选项
          this.extractYearOptions(result.records || []);
        } else {
          this.$message.error(res.message || '查询明细失败');
        }
      } catch (e) {
        this.$message.error('查询明细失败');
      } finally {
        this.loading = false;
      }
    },

    /** 从数据中提取年度选项 */
    extractYearOptions(records) {
      const years = new Set(this.yearOptions);
      records.forEach(r => {
        if (r.feeYear) years.add(r.feeYear);
      });
      // 生成近5年选项
      const currentYear = new Date().getFullYear();
      for (let i = 0; i < 5; i++) {
        years.add(String(currentYear - i));
      }
      this.yearOptions = Array.from(years).sort().reverse();
    },

    /** 搜索 */
    handleSearch() {
      this.pagination.current = 1;
      this.loadDetails();
    },

    /** 分页变化 */
    handleTableChange(pagination) {
      this.pagination.current = pagination.current;
      this.pagination.pageSize = pagination.pageSize;
      this.loadDetails();
    },

    /** 格式化金额 */
    formatMoney(val) {
      if (val === null || val === undefined) return '0.00';
      return Number(val).toFixed(2);
    },
  },
};
</script>

<style lang="stylus" scoped>
@import '~styles/varibles.styl';

.tuition-fee {
  padding: 20px;
}

.module-closed {
  padding: 60px 0;
  text-align: center;
}

.summary-cards {
  margin-bottom: 24px;

  .card-item {
    background: #f7f8fa;
    border-radius: 8px;
    padding: 20px;
    text-align: center;

    .card-label {
      font-size: 14px;
      color: #666;
      margin-bottom: 8px;
    }

    .card-value {
      font-size: 24px;
      font-weight: bold;
      color: #333;

      &.paid {
        color: #1890ff;
      }

      &.refund {
        color: #52c41a;
      }

      &.owe {
        color: #f5222d;
      }
    }
  }
}

.filter-bar {
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.detail-table {
  background: #fff;
}
</style>



// WEBPACK FOOTER //
// src/pages/PersonalCenters/components/TuitionFee.vue